import { Link, useNavigate } from "@tanstack/react-router";
import { useCallback, type AnchorHTMLAttributes, type CSSProperties, type ReactNode } from "react";
import type { PurchasingTab } from "../purchasing/PurchasingPage.tsx";
import type { TodayTab } from "../stock/TodayPage.tsx";

/*
 * Куда ведёт пункт меню, плитка стола или команда палитры. Разделы без
 * своих экранов пока на /s/$sectionId, склад уже на своих маршрутах.
 */
export type Target =
  | { kind: "section"; id: string }
  | { kind: "stock" }
  | { kind: "shelf" }
  | { kind: "today"; tab: TodayTab }
  | { kind: "requests" }
  | { kind: "purchasing"; tab: PurchasingTab }
  | { kind: "line" }
  | { kind: "myOps" }
  | { kind: "unit" }
  | { kind: "catalog" }
  | { kind: "partners" }
  | { kind: "sales" }
  | { kind: "costing" };

/** Путь без поиска: по нему нижняя панель понимает, какой пункт открыт. */
export function targetPath(target: Target): string {
  switch (target.kind) {
    case "section":
      return `/s/${target.id}`;
    case "stock":
      return "/stock";
    case "shelf":
      return "/stock/shelf";
    case "today":
      return "/stock/today";
    case "requests":
      return "/stock/requests";
    case "purchasing":
      return "/purchasing";
    case "line":
      return "/production/line";
    case "myOps":
      return "/production/my";
    case "unit":
      return "/production/unit";
    case "catalog":
      return "/catalog/products";
    case "partners":
      return "/partners";
    case "sales":
      return "/sales";
    case "costing":
      return "/costing/close";
  }
}

type AnchorProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href" | "children" | "style" | "target"> & {
  style?: CSSProperties;
  "data-tone"?: string;
};

export function TargetLink({ dest: target, children, ...common }: AnchorProps & { dest: Target; children: ReactNode }) {
  switch (target.kind) {
    case "section":
      return (
        <Link to="/s/$sectionId" params={{ sectionId: target.id }} {...common}>
          {children}
        </Link>
      );
    case "stock":
      return (
        <Link to="/stock" {...common}>
          {children}
        </Link>
      );
    case "shelf":
      return (
        <Link to="/stock/shelf" {...common}>
          {children}
        </Link>
      );
    case "today":
      return (
        <Link to="/stock/today" search={{ tab: target.tab }} {...common}>
          {children}
        </Link>
      );
    case "requests":
      return (
        <Link to="/stock/requests" {...common}>
          {children}
        </Link>
      );
    case "purchasing":
      return (
        <Link to="/purchasing" search={{ tab: target.tab }} {...common}>
          {children}
        </Link>
      );
    case "line":
      return (
        <Link to="/production/line" {...common}>
          {children}
        </Link>
      );
    case "myOps":
      return (
        <Link to="/production/my" {...common}>
          {children}
        </Link>
      );
    case "unit":
      return (
        <Link to="/production/unit" {...common}>
          {children}
        </Link>
      );
    case "catalog":
      return (
        <Link to="/catalog/products" {...common}>
          {children}
        </Link>
      );
    case "partners":
      return (
        <Link to="/partners" {...common}>
          {children}
        </Link>
      );
    case "sales":
      return (
        <Link to="/sales" {...common}>
          {children}
        </Link>
      );
    case "costing":
      return (
        <Link to="/costing/close" {...common}>
          {children}
        </Link>
      );
  }
}

export function useGoTo(): (target: Target) => void {
  const navigate = useNavigate();
  return useCallback(
    (target: Target) => {
      switch (target.kind) {
        case "section":
          void navigate({ to: "/s/$sectionId", params: { sectionId: target.id } });
          return;
        case "stock":
          void navigate({ to: "/stock" });
          return;
        case "shelf":
          void navigate({ to: "/stock/shelf" });
          return;
        case "today":
          void navigate({ to: "/stock/today", search: { tab: target.tab } });
          return;
        case "requests":
          void navigate({ to: "/stock/requests" });
          return;
        case "purchasing":
          void navigate({ to: "/purchasing", search: { tab: target.tab } });
          return;
        case "line":
          void navigate({ to: "/production/line" });
          return;
        case "myOps":
          void navigate({ to: "/production/my" });
          return;
        case "unit":
          void navigate({ to: "/production/unit" });
          return;
        case "catalog":
          void navigate({ to: "/catalog/products" });
          return;
        case "partners":
          void navigate({ to: "/partners" });
          return;
        case "sales":
          void navigate({ to: "/sales" });
          return;
        case "costing":
          void navigate({ to: "/costing/close" });
          return;
      }
    },
    [navigate],
  );
}
